import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { snapOrFromTo, snapOrTo } from 'animations/snapTween';
import { GAME_HAND_PHASE } from 'constants/gameConstants';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import gsap from 'gsap';
import { useTableSnap } from 'hooks/useTableSnap';
import { useTableSelector } from 'hooks/useTableSelector';

import { isFaceDownCard, mapToCard } from 'components/Card';
import SmallCard from 'components/Card/SmallCard';

type PovCardsProps = {
  cards: string[];
  elevation?: number;
  isFaceDown: boolean;
  isDimmed?: (card: string) => boolean;
  shouldAnimateDeal: boolean;
  shouldCollect: boolean;
  style?: CSSProperties;
};

export const PovCards = ({
  cards,
  elevation,
  isFaceDown,
  isDimmed = (_: string) => false,
  shouldAnimateDeal,
  shouldCollect,
  style
}: PovCardsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const shouldSnap = useTableSnap();
  const isMountedRef = useRef(false);
  const hasDealtCurrentHandRef = useRef(false);
  const hasStartedCollectRef = useRef(false);
  const wasFaceDownRef = useRef(isFaceDown);
  const hand = useTableSelector((table) => table.game.hand);
  const handNum = Number(hand);
  const hasActiveHandPhase = Number.isFinite(handNum);
  // A completed hand's Table Snapshot can legitimately have game.hand below
  // PRE_FLOP_BETTING_ROUND - e.g. an uncontested all-fold win with no board
  // ever dealt reports hand:'1'. The gates below exist to hide the live deal
  // animation's pre-deal frame, not to hide a genuinely concluded hand's own
  // (already-resolved) cards; bypass them in snapshot mode.
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  // From DEAL_CARDS (hand 4), the frame the table deals on, not hand 5: the
  // server sends this seat face-down placeholders there, so it is dealt together
  // with every other seat and turned face up when the real cards arrive at hand 5
  // (EN-10985). A client that never sees hand 4 still deals at hand 5, as before.
  const isBelowActiveHandPhase =
    !isHandHistorySnapshot && (!hasActiveHandPhase || handNum < GAME_HAND_PHASE.DEAL_CARDS);
  const visibleCards = cards.filter((card: string) => !!card);
  const cardKey = visibleCards.join('|');
  const isPlaceholderOnly = visibleCards.length > 0 && visibleCards.every(isFaceDownCard);
  // Read by tween callbacks and effect cleanups, which outlive the render they
  // were created in: "are the cards on screen still the deal-frame placeholders?"
  const isPlaceholderOnlyRef = useRef(isPlaceholderOnly);
  isPlaceholderOnlyRef.current = isPlaceholderOnly;
  const dealInFlightRef = useRef(false);
  const dealtPlaceholdersRef = useRef(false);
  const shouldDealFaceDownFirst = !isFaceDown && shouldAnimateDeal;
  const [visuallyFaceDown, setVisuallyFaceDown] = useState(isFaceDown || shouldDealFaceDownFirst);
  const [hasCollected, setHasCollected] = useState(false);
  const revealAfterDealRef = useRef(shouldDealFaceDownFirst);

  const getTableCenter = () => {
    const tableRoot = containerRef.current?.closest('[data-table-root]');
    const tableRect = tableRoot?.getBoundingClientRect();
    return {
      x: tableRect ? tableRect.left + tableRect.width / 2 : window.innerWidth / 2,
      y: tableRect ? tableRect.top + tableRect.height / 2 : window.innerHeight / 2
    };
  };

  // After a face-down deal on hand 4: flip the cards over to show the real ones.
  const turnFaceUp = (cardEls: NodeListOf<Element>) => {
    if (shouldSnap()) {
      revealAfterDealRef.current = false;
      gsap.set(cardEls, { rotationY: 0, scale: 1 });
      setVisuallyFaceDown(false);
      return undefined;
    }
    // revealAfterDealRef stays set until the flip is edge-on: the face-down
    // effect above reads it, and clearing it now would let that effect show the
    // faces at once, before the cards have turned.
    const tl = flipCards(cardEls, () => {
      revealAfterDealRef.current = false;
      if (isMountedRef.current) setVisuallyFaceDown(false);
    });
    return () => {
      tl.kill();
    };
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (shouldCollect) return;

    const cardEls = containerRef.current?.querySelectorAll('.pov-card');

    if (wasFaceDownRef.current === isFaceDown || !cardEls || cardEls.length === 0) {
      if (revealAfterDealRef.current && !isFaceDown) {
        wasFaceDownRef.current = isFaceDown;
        return;
      }
      setVisuallyFaceDown(isFaceDown);
      return;
    }

    wasFaceDownRef.current = isFaceDown;
    gsap.killTweensOf(cardEls);

    // Snapping (table switch / resync): the reveal already happened — show the
    // cards in their revealed/hidden end state without flipping them again.
    if (shouldSnap()) {
      gsap.set(cardEls, { rotationY: 0, scale: 1 });
      setVisuallyFaceDown(isFaceDown);
      return;
    }

    const tl = flipCards(cardEls, () => {
      if (isMountedRef.current) {
        setVisuallyFaceDown(isFaceDown);
      }
    });

    return () => {
      tl.kill();
    };
    // shouldSnap is read inside the effect on purpose — see hooks/useTableSnap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFaceDown, cardKey, shouldCollect]);

  // Safety net for laggy devices. The flip/deal reveal above sets visuallyFaceDown
  // only from a gsap onComplete callback, which never fires if the tween is killed
  // by a re-render (tl.kill / killTweensOf in the cleanups) before it finishes —
  // extra re-renders on a laggy device do exactly that, leaving the player's own
  // cards stuck showing the HJ card back at showdown. Reconcile the visual state to
  // the data-driven isFaceDown a beat after any mismatch, so the cards always end
  // in their correct revealed/hidden state even when the animation was interrupted.
  // The delay clears the longest normal animation window (deal ~0.94s), so on a
  // healthy device the animation's own onComplete wins and this never preempts it.
  useEffect(() => {
    // Placeholders have no face to show; turning them "face up" early would only
    // spoil the flip when the real cards arrive.
    if (shouldCollect || visuallyFaceDown === isFaceDown || isPlaceholderOnly) return;
    const id = window.setTimeout(() => {
      if (!isMountedRef.current) return;
      // A killed deal/flip tween leaves a partial gsap transform (part-rotated,
      // part-moved) on the cards, so reconciling the face-down state alone would
      // still render them askew. Snap the transform back to its resting layout.
      const cardEls = containerRef.current?.querySelectorAll('.pov-card');
      if (cardEls && cardEls.length) gsap.set(cardEls, { clearProps: 'transform,opacity' });
      setVisuallyFaceDown(isFaceDown);
    }, 1200);
    return () => window.clearTimeout(id);
  }, [isFaceDown, visuallyFaceDown, shouldCollect, cardKey, isPlaceholderOnly]);

  // Safety net for laggy devices, collect side. The collect animation above only
  // hides the cards (setHasCollected) from the last card's gsap onComplete. If a
  // re-render kills that tween first (killTweensOf in the cleanups), onComplete
  // never fires and the cards stay stuck mid-flight — rotated and shoved toward
  // table center but still visible — at showdown. Force the collected/hidden end
  // state a beat after collect starts so the cards always clear even when the
  // animation was interrupted. The delay clears the collect window (0.55s +
  // per-card delay), so on a healthy device the animation's own onComplete wins.
  useEffect(() => {
    if (!shouldCollect || hasCollected) return;
    const id = window.setTimeout(() => {
      if (isMountedRef.current) setHasCollected(true);
    }, 1200);
    return () => window.clearTimeout(id);
  }, [shouldCollect, hasCollected, cardKey]);

  useLayoutEffect(() => {
    if (isBelowActiveHandPhase) {
      hasDealtCurrentHandRef.current = false;
      dealtPlaceholdersRef.current = false;
      dealInFlightRef.current = false;
      hasStartedCollectRef.current = false;
      revealAfterDealRef.current = !isFaceDown;
      setVisuallyFaceDown(true);
      setHasCollected(false);
      return;
    }

    if (!containerRef.current || !cardKey) {
      hasDealtCurrentHandRef.current = false;
      dealtPlaceholdersRef.current = false;
      dealInFlightRef.current = false;
      hasStartedCollectRef.current = false;
      revealAfterDealRef.current = false;
      setHasCollected(false);
      return;
    }

    const cardEls = containerRef.current.querySelectorAll('.pov-card');

    if (shouldCollect) {
      if (hasStartedCollectRef.current) return;

      hasStartedCollectRef.current = true;
      revealAfterDealRef.current = false;
      setHasCollected(false);
      const tableCenter = getTableCenter();

      gsap.killTweensOf(cardEls);
      cardEls.forEach((element, index) => {
        const cardRect = element.getBoundingClientRect();
        const cardCenter = {
          x: cardRect.left + cardRect.width / 2,
          y: cardRect.top + cardRect.height / 2
        };
        const spinDirection = index % 2 === 0 ? -1 : 1;

        // Snapping (table switch / resync): the hand is already over — land on
        // the collected end state (onComplete still flips hasCollected).
        snapOrTo(shouldSnap(), element, {
          x: tableCenter.x - cardCenter.x,
          y: tableCenter.y - cardCenter.y,
          opacity: 0,
          scale: 0.72,
          rotation: spinDirection * 420,
          rotationY: spinDirection * 75,
          duration: 0.55,
          delay: index * 0.08,
          ease: 'power2.in',
          force3D: true,
          onComplete: () => {
            if (index === cardEls.length - 1 && isMountedRef.current) {
              setHasCollected(true);
            }
          }
        });
      });
      return () => {
        gsap.killTweensOf(cardEls);
      };
    }

    // A new hand's cards can land while the previous hand's collected/hidden
    // state is still latched. A background (parked) table receives compacted,
    // newest-wins snapshots (BackgroundTableBuffer, at most one per 2s), and a
    // hidden tab / backgrounded app flushes its whole backlog as one compacted
    // batch, so the phases that normally clear the latch — hand < 5, empty
    // cards, the hand-5 deal — may never be observed and the new cards would
    // mount into a visibility:hidden wrapper for the whole hand. Cards in a live
    // betting phase belong to the current hand, not the one that was collected:
    // drop the latch and the collect transform, then fall through so a hand-5
    // deal still animates normally. See docs/investigations/20260907-multitable-
    // pov-hole-cards-hidden-after-background-collect.md.
    if (hasStartedCollectRef.current) {
      hasStartedCollectRef.current = false;
      hasDealtCurrentHandRef.current = false;
      dealtPlaceholdersRef.current = false;
      dealInFlightRef.current = false;
      revealAfterDealRef.current = false;
      gsap.set(cardEls, { clearProps: 'transform,opacity' });
      setHasCollected(false);
      setVisuallyFaceDown(isFaceDown);
    }

    // Dealt face down on the deal frame (hand 4), and the real cards have now
    // arrived (hand 5): turn them face up. A deal still flying in finishes first
    // and turns them up itself (see its onComplete).
    if (
      hasDealtCurrentHandRef.current &&
      dealtPlaceholdersRef.current &&
      !isPlaceholderOnly &&
      !isFaceDown
    ) {
      dealtPlaceholdersRef.current = false;
      if (dealInFlightRef.current) return;
      return turnFaceUp(cardEls);
    }

    const isDealFrame =
      handNum === GAME_HAND_PHASE.DEAL_CARDS || handNum === GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND;
    if (isFaceDown || !isDealFrame || !shouldAnimateDeal || hasDealtCurrentHandRef.current) {
      return;
    }

    const dealsPlaceholders = isPlaceholderOnly;
    hasDealtCurrentHandRef.current = true;
    dealtPlaceholdersRef.current = dealsPlaceholders;
    dealInFlightRef.current = true;
    hasStartedCollectRef.current = false;
    revealAfterDealRef.current = true;
    setHasCollected(false);
    setVisuallyFaceDown(true);
    const tableCenter = getTableCenter();

    gsap.killTweensOf(cardEls);
    cardEls.forEach((element, index) => {
      const cardRect = element.getBoundingClientRect();
      const cardCenter = {
        x: cardRect.left + cardRect.width / 2,
        y: cardRect.top + cardRect.height / 2
      };
      const spinDirection = index % 2 === 0 ? 1 : -1;

      // Snapping: the player's own cards were dealt before they switched in —
      // place them face-up at rest (the end state) with no fly-in.
      snapOrFromTo(
        shouldSnap(),
        element,
        {
          x: tableCenter.x - cardCenter.x,
          y: tableCenter.y - cardCenter.y,
          opacity: 0,
          scale: 0.7,
          rotation: spinDirection * 640,
          rotationY: spinDirection * 80,
          transformPerspective: 700
        },
        {
          x: 0,
          y: 0,
          opacity: 1,
          scale: 1,
          rotation: 0,
          rotationY: 0,
          duration: 0.7,
          delay: index * 0.12,
          ease: 'power3.out',
          force3D: true,
          onComplete: () => {
            if (index !== cardEls.length - 1) return;
            dealInFlightRef.current = false;
            if (isFaceDown || !isMountedRef.current) return;
            // Placeholders stay face down: there is no face until hand 5.
            if (isPlaceholderOnlyRef.current) return;
            // The real cards arrived while the placeholders were still flying in.
            if (dealsPlaceholders) {
              turnFaceUp(cardEls);
              return;
            }
            revealAfterDealRef.current = false;
            setVisuallyFaceDown(false);
          },
          // onComplete is skipped when the deal tween is killed by a re-render
          // (killTweensOf in the cleanups). On a laggy device that leaves the
          // player's own cards stuck face-down and mid-fly (skewed) until the
          // hand ends. gsap fires onInterrupt the instant a tween is killed, so
          // reconcile the reveal here too — snap this card back to its resting
          // position and reveal it immediately, without waiting on a timer a
          // re-render storm can keep resetting.
          onInterrupt: () => {
            dealInFlightRef.current = false;
            if (isFaceDown || !isMountedRef.current) return;
            gsap.set(element, { x: 0, y: 0, opacity: 1, scale: 1, rotation: 0, rotationY: 0 });
            // Placeholders land face down; the real cards' arrival turns them up.
            if (isPlaceholderOnlyRef.current) return;
            revealAfterDealRef.current = false;
            setVisuallyFaceDown(false);
          }
        }
      );
    });
    return () => {
      // An Omaha deal (four cards, ~1.06 s) can outlast the 1 s tick, so the real
      // cards may arrive mid-flight. That re-runs this effect; let the deal land
      // rather than snapping it to rest -- its onComplete turns the cards up.
      if (dealsPlaceholders && dealInFlightRef.current && !isPlaceholderOnlyRef.current) return;
      gsap.killTweensOf(cardEls);
    };
    // shouldSnap is read inside the effect on purpose — see hooks/useTableSnap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardKey, handNum, isBelowActiveHandPhase, isFaceDown, shouldAnimateDeal, shouldCollect]);

  // Avoid a one-frame flash at the seat before the visual deal animation window
  // - live play only; see isBelowActiveHandPhase above for the snapshot bypass.
  if (isBelowActiveHandPhase && !shouldCollect) {
    return null;
  }

  return (
    <div ref={containerRef} style={{ ...povCardsStyle, ...style }}>
      {visibleCards.map((card: string, index: number) => {
        const pokerCard = mapToCard(card) ?? {};

        return (
          <div
            className='pov-card'
            key={`pov-card-${index}`}
            style={{
              ...povCardStyle,
              ...(isDimmed(card) ? dimmedPovCardStyle : {}),
              ...(hasCollected ? collectedPovCardStyle : {})
            }}>
            <SmallCard
              elevation={elevation}
              {...pokerCard}
              isFaceDown={visuallyFaceDown || isFaceDownCard(card)}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * Turn the cards over: each rotates edge-on, `onEdge` swaps the face it shows
 * (once per card, while it is edge-on), and it rotates back. If the flip is
 * killed mid-tween the cards would be left edge-on and never reconciled, since
 * the swap only runs from the per-card callback, so an interrupt squares them up
 * and swaps right away.
 */
const flipCards = (cardEls: NodeListOf<Element>, onEdge: () => void) => {
  const tl = gsap.timeline({
    onComplete: () => {
      gsap.set(cardEls, { rotationY: 0 });
    },
    onInterrupt: () => {
      gsap.set(cardEls, { rotationY: 0 });
      onEdge();
    }
  });

  cardEls.forEach((element, index) => {
    tl.to(
      element,
      {
        rotationY: 88,
        scale: 0.96,
        duration: 0.16,
        delay: index * 0.04,
        ease: 'power2.in',
        transformPerspective: 700,
        force3D: true,
        onComplete: onEdge
      },
      0
    ).to(
      element,
      {
        rotationY: 0,
        scale: 1,
        duration: 0.2,
        ease: 'power2.out',
        force3D: true
      },
      0.16 + index * 0.04
    );
  });

  return tl;
};

const povCardsStyle: CSSProperties = {
  display: 'flex',
  gap: '4px',
  overflow: 'visible'
};

const povCardStyle: CSSProperties = {
  transformOrigin: 'center center',
  willChange: 'transform, opacity'
};

const dimmedPovCardStyle: CSSProperties = {
  filter: 'brightness(0.45)'
};

const collectedPovCardStyle: CSSProperties = {
  visibility: 'hidden',
  pointerEvents: 'none'
};
